'use client';

import { FC } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Scale } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { CompareEntryPoint, CompareType } from '@/hotdesk-ui/enum/compare';
import { useCompareConfig } from '@/hotdesk-ui/hooks/compare/useCompareConfig';
import { useCompareTracking } from '@/hotdesk-ui/hooks/compare/useCompareTracking';
import { buildCompareHref } from '@/hotdesk-ui/lib/compare/url-state';
import styles from './CompareThisButton.module.scss';
interface Props {
  type: CompareType;
  id: number | null | undefined;
  /** Shown in the table's "Back to [name]". */
  name: string;
}
const ENTRY_POINT: Record<CompareType, CompareEntryPoint> = {
  [CompareType.WORKSPACE]: CompareEntryPoint.WORKSPACE_DETAILS,
  [CompareType.LISTING]: CompareEntryPoint.LISTING_DETAILS
};

/**
 * "Compare this workspace" / "Compare this listing" pill after the rating and
 * guests pills on a details page. Opens the comparison with only this space,
 * in the first column; the table's back button reads "Back to [name]" and
 * returns to this page as it is (path and query). Remote config can switch
 * each details page off on its own.
 */
const CompareThisButton: FC<Props> = ({
  type,
  id,
  name
}) => {
  const t = useTranslations('compare.details');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    enabled,
    entryPoints
  } = useCompareConfig();
  const track = useCompareTracking(type);
  const entryPoint = ENTRY_POINT[type];
  if (!enabled || !id || !entryPoints.includes(entryPoint)) return null;
  const query = searchParams.toString();
  const href = buildCompareHref(type, {
    items: [id],
    from: entryPoint,
    back: query ? `${pathname}?${query}` : pathname,
    backName: name
  });
  return <Link href={href}
  // One noindex URL per details page: not worth crawling or prefetching.
  rel="nofollow" prefetch={false} className={styles.pill} onClick={() => track('compare_details_clicked', 1, {
    page: entryPoint,
    item_id: id
  })} data-sentry-element="Link" data-sentry-component="CompareThisButton" data-sentry-source-file="CompareThisButton.tsx">
            <Scale size={16} strokeWidth={2} aria-hidden="true" data-sentry-element="Scale" data-sentry-source-file="CompareThisButton.tsx" />
            {t(type === CompareType.WORKSPACE ? 'workspace' : 'listing')}
        </Link>;
};
export default CompareThisButton;